import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import styled from "@emotion/styled";
import { APPLICANT_TYPES, APPLICATION_STEPS } from "constants/index";
import {
  ApplicationRequirements,
  ApplicantAbandon,
  ApplicantContactUs,
  ApplicantProgress,
  ApplicationEditableQuestionnaire,
  ApplicationEditableRate,
  ApplicationStateBanner,
} from "components/Missions/Applicants";
import { Grid } from "components/Containers";
import Card from "components/Card";
import { RoleDetails } from "components/Missions/Roles";
import { CandidateCodeChallenge, CandidateInterview } from "components/Candidate";
import { useAuth } from "hooks";
import DisplayBox from "components/DisplayBox";
import { MissionAboutClient } from "components/Missions";
import { Spinner } from "components/Loader";
import { TagComplete, TagDuration, TagOptional } from "components/Tags";
import { MISSION_ROLE_STATES } from "constants";
import { isApplicationStepOptional } from "utils";

/**
 * ApplicationForm
 *
 * @param {Object}    selected
 * @param {Boolean}   loading
 * @param {Boolean}   rateEditing
 * @param {Function}  setRateEditing
 * @param {Boolean}   questionnaireEditing
 * @param {Function}  setQuestionnaireEditing
 * @param {Boolean}   hideAbandon
 * @param {Boolean}   hideProgress
 * @param {Boolean}   hideRequirements
 * @param {Boolean}   hideDetails
 * @param {Boolean}   hideContactUs
 */
const ApplicationForm = ({
  selected,
  loading,
  rateEditing,
  setRateEditing,
  questionnaireEditing,
  setQuestionnaireEditing,
  requirementsEditing,
  setRequirementsEditing,
  hideAbandon,
  hideProgress,
  hideRequirements,
  hideDetails,
  hideContactUs,
}) => {
  const { profileId, canViewEditRate, isMember } = useAuth();
  const roleChallenges = selected?.challengesForApplicant || [];
  const codeChallengeRequirement = selected?.applicationChecklist?.find(
    (step) => step.name === APPLICATION_STEPS.code_challenge
  );
  const codeChallengeCompleted = codeChallengeRequirement?.fulfilled;
  const isCodeChallengeOptional = isApplicationStepOptional(
    selected?.missionRole?.applicationSteps,
    APPLICATION_STEPS.code_challenge
  );
  const disabled =
    selected?.missionRole?.state === MISSION_ROLE_STATES.closed || selected?.state === APPLICANT_TYPES.withdrawn;
  const isPending = selected?.state === APPLICANT_TYPES.pending;

  return (
    <Grid topMargin>
      {loading && (
        <Grid.col start={1} end={13}>
          <CenteredSpinner>
            <Spinner />
          </CenteredSpinner>
        </Grid.col>
      )}
      {selected && (
        <>
          {isMember && <ApplicationStateBanner application={selected} />}
          <Grid.col start={1} end={hideProgress ? 13 : 9}>
            <Grid>
              {!hideAbandon && isPending && (
                <Grid.col start={1} end={13}>
                  <ApplicantAbandon application={selected} />
                </Grid.col>
              )}
              {!hideRequirements && (
                <Grid.col start={1} end={13}>
                  <Card>
                    <DisplayBox
                      title={<FormattedMessage id="MissionApplications.Application.Requirements.Title" />}
                      description={
                        <p>
                          <FormattedMessage id="MissionApplications.Application.Requirements.Text" />
                        </p>
                      }
                    />
                  </Card>
                </Grid.col>
              )}
              {selected.missionRole?.requirements?.length > 0 && (
                <Grid.col start={1} end={13}>
                  <ApplicationRequirements
                    application={selected}
                    title="Missions.Applicants.RoleRequirements.Title"
                    requirementsEditing={requirementsEditing}
                    setRequirementsEditing={setRequirementsEditing}
                    showMissingRequirements={isPending}
                    isEditable={!selected.appliedAt && !disabled}
                  />
                </Grid.col>
              )}
              {canViewEditRate && (
                <ApplicationEditableRate
                  missionRoleApplicant={selected}
                  rateEditing={rateEditing}
                  setRateEditing={setRateEditing}
                  isEditable={!selected.appliedAt && !disabled}
                />
              )}
              <ApplicationEditableQuestionnaire
                missionRoleApplicant={selected}
                questionnaireEditing={questionnaireEditing}
                setQuestionnaireEditing={setQuestionnaireEditing}
                isEditable={!selected.appliedAt && !disabled}
              />
              {codeChallengeRequirement && (
                <Grid.col start={1} end={13}>
                  <Card>
                    <CandidateCodeChallenge
                      profileId={profileId}
                      applicantId={selected.id}
                      {...(roleChallenges?.length > 0 && {
                        showSkills: false,
                        role: selected.missionRole,
                        challengeIds: roleChallenges.map((c) => c.id),
                        resultsPerPage: roleChallenges.length,
                        subtitle: !codeChallengeCompleted ? (
                          isCodeChallengeOptional ? (
                            <TagOptional />
                          ) : (
                            <TagDuration text={"~2 hours"} />
                          )
                        ) : (
                          <TagComplete />
                        ),
                      })}
                    />
                  </Card>
                </Grid.col>
              )}
              {selected?.missionRole?.interview?.id && isMember && (
                <Grid.col start={1} end={13}>
                  <Card>
                    <CandidateInterview missionRoleId={selected.missionRole.id} profileId={profileId} />
                  </Card>
                </Grid.col>
              )}
              {!hideDetails && (
                <>
                  <Grid.col start={1} end={13}>
                    <Card>
                      <RoleDetails
                        role={selected?.missionRole}
                        mission={selected?.missionRole?.mission}
                        loading={loading}
                      />
                    </Card>
                  </Grid.col>
                  <Grid.col start={1} end={13}>
                    <Card>
                      <MissionAboutClient mission={selected?.missionRole?.mission} />
                    </Card>
                  </Grid.col>
                </>
              )}
            </Grid>
          </Grid.col>
          <Grid.col start={9} end={13}>
            <Grid>
              {!hideProgress && (
                <Grid.col start={1} end={13}>
                  <Card>
                    <ApplicantProgress
                      missionRoleApplicant={selected}
                      setRateEditing={setRateEditing}
                      setQuestionnaireEditing={setQuestionnaireEditing}
                      setRequirementsEditing={setRequirementsEditing}
                    />
                  </Card>
                </Grid.col>
              )}
              {!hideContactUs && (
                <Grid.col start={1} end={13}>
                  <ApplicantContactUs />
                </Grid.col>
              )}
            </Grid>
          </Grid.col>
        </>
      )}
    </Grid>
  );
};

ApplicationForm.defaultProps = {
  hideContactUs: true,
};

ApplicationForm.propTypes = {
  selected: PropTypes.object,
  loading: PropTypes.bool,
  hideProgress: PropTypes.bool,
  hideRequirements: PropTypes.bool,
  hideDetails: PropTypes.bool,
  hideAbandon: PropTypes.bool,
  hideContactUs: PropTypes.bool,
  rateEditing: PropTypes.bool,
  setRateEditing: PropTypes.func,
  questionnaireEditing: PropTypes.bool,
  setQuestionnaireEditing: PropTypes.func,
  requirementsEditing: PropTypes.bool,
  setRequirementsEditing: PropTypes.func,
};

const CenteredSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

export default ApplicationForm;
